import { Container, Graphics, Rectangle, Renderer, Sprite, Texture } from "pixi.js"
import { NumberedTileMeta, TileSize } from "game-common/models"
import { contentSheetResource, contentTextureGeneration } from "../content_textures"

export interface RootEdge { x: number; y: number }
export interface RootBlade extends RootEdge { height: number; color: number }

/** Bottom-most opaque pixel of each root column, excluding the canopy above the last tile row. */
export const treeRootContour = (rgba: ArrayLike<number>, width: number, height: number): RootEdge[] => {
    const edges: RootEdge[] = []
    for (let x = 0; x < width; x++) for (let y = height - 1; y >= Math.max(0, height - TileSize); y--) {
        if (rgba[(y * width + x) * 4 + 3] > 128) { edges.push({ x, y }); break }
    }
    return edges
}

const noise = (x: number, y: number, seed: number) => {
    let h = Math.imul(x + seed, 374761393) ^ Math.imul(y, 668265263)
    h = Math.imul(h ^ h >>> 13, 1274126177)
    return ((h ^ h >>> 16) >>> 0) / 4294967295
}

// One shared palette and density for all grass surfaces.
const PALETTE = [0x3e872a, 0x4d932a, 0x6a9e31]
export const rootFringeBlades = (edges: RootEdge[]): RootBlade[] => edges.flatMap(edge => {
    if (edge.x % 2 || noise(edge.x, edge.y, 0) > 0.58) return []
    const variation = noise(edge.x, edge.y, 71)
    return [{ x: edge.x, y: edge.y + 1, height: 2 + Math.floor(variation * 4),
        color: PALETTE[Math.floor(variation * 3)] }]
})

/** A single ground sample at the lowest root, in the composite's local coordinates. */
export const rootGroundPoint = (edges: RootEdge[]): RootEdge => {
    const bottom = Math.max(...edges.map(edge => edge.y))
    const roots = edges.filter(edge => edge.y >= bottom - 2)
    return { x: (Math.min(...roots.map(edge => edge.x)) + Math.max(...roots.map(edge => edge.x))) / 2,
        y: bottom + 2 }
}

const contours = new Map<string, RootEdge[]>()
let contourGeneration = -1

/** Read original atlas pixels once per tree variant, never GPU-read back a generated texture. */
export const treeRootEdges = (tilesetId: string, compositeId: string, columns: number,
    children: NumberedTileMeta[]): RootEdge[] | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== contourGeneration) { contours.clear(); contourGeneration = generation }
    const key = `${tilesetId}:${compositeId}`
    if (contours.has(key)) return contours.get(key)
    const frames = contentSheetResource(tilesetId)?.textures
    if (!frames || !children.length) return undefined
    const cells = children.map(child => ({ tile: child.tileNumber, x: (child.tileNumber - 1) % columns, y: Math.floor((child.tileNumber - 1) / columns) }))
    const left = Math.min(...cells.map(c => c.x)), top = Math.min(...cells.map(c => c.y))
    const canvas = document.createElement("canvas")
    canvas.width = (Math.max(...cells.map(c => c.x)) - left + 1) * TileSize
    canvas.height = (Math.max(...cells.map(c => c.y)) - top + 1) * TileSize
    const context = canvas.getContext("2d")
    if (!context) return undefined
    for (const cell of cells) {
        const texture = frames[`${tilesetId}-${cell.tile}`]
        const source = (texture?.baseTexture?.resource as any)?.source
        if (!source) return undefined
        const frame = texture.frame
        context.drawImage(source, frame.x, frame.y, frame.width, frame.height,
            (cell.x - left) * TileSize, (cell.y - top) * TileSize, TileSize, TileSize)
    }
    const edges = treeRootContour(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height)
    contours.set(key, edges)
    return edges
}

// Share the baked texture between every tree of the same variant. No extra display objects
// or terrain polling survive into the frame loop; a map redraw makes the grass decision again.
type TextureBaker = Pick<Renderer, "generateTexture">
const baked = new WeakMap<Texture, { renderer: TextureBaker; texture: Texture }>()
export const treeRootFringeTexture = (tree: Texture, edges: RootEdge[], renderer: TextureBaker): Texture => {
    const cached = baked.get(tree)
    if (cached?.renderer === renderer && !cached.texture.baseTexture.destroyed) return cached.texture
    const surface = new Container()
    surface.addChild(new Sprite(tree))
    const fringe = new Graphics()
    surface.addChild(fringe)
    for (const blade of rootFringeBlades(edges)) {
        fringe.beginFill(blade.color)
        fringe.drawRect(blade.x, blade.y - blade.height + 1, 1, blade.height)
        fringe.drawRect(blade.x + (blade.x % 4 ? -1 : 1), blade.y - blade.height, 1, 1)
        fringe.endFill()
    }
    const texture = renderer.generateTexture(surface, { region: new Rectangle(0, 0, tree.width, tree.height) })
    surface.destroy({ children: true })
    baked.set(tree, { renderer, texture })
    return texture
}
